import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth-context';
import {
  Check,
  Crown,
  Star,
  Zap,
  Truck,
  Gift,
  Clock,
  Headphones,
  Loader2,
  Building2,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { resolveMembershipError, getMembershipTierLabel, getMembershipTierDisplayName, getMembershipTierFeatures } from '@/lib/membership-utils';
import { buildPathForLocale } from '@/lib/intl-seo';
import { PageSeo } from '@/components/seo/PageSeo';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 20;

const emptyCard = {
  holder: '',
  number: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: '',
  installment: '1',
};

const MembershipPage = () => {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [userPoints, setUserPoints] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [ziraatCard, setZiraatCard] = useState(emptyCard);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const pollRef = useRef(0);
  const timerRef = useRef(null);

  const isTurkish = i18n.language === 'tr';
  const lang = (i18n.language || 'tr').split('-')[0];
  const membershipPath = buildPathForLocale(lang, ['membership']);
  const paymentRef = searchParams.get('order_id') || searchParams.get('session_id');
  const urlError = searchParams.get('error');
  const showPaymentBanner = Boolean(paymentRef || urlError || location.pathname.includes('/membership/success'));

  useEffect(() => {
    fetchData();
  }, [user?.id]);

  useEffect(() => {
    if (!showPaymentBanner) return;

    if (urlError && !paymentRef) {
      setPaymentStatus('failed');
      setPaymentError(decodeURIComponent(urlError));
      return;
    }

    if (!paymentRef) return;

    if (authLoading) return;

    const syncMembership = async () => {
      if (!user || !paymentRef.startsWith('MEM-')) return;
      try {
        await api.get(`/membership/subscription/status/${paymentRef}`);
        await fetchData();
      } catch (e) {
        console.error('Membership sync failed:', e);
      }
    };

    const checkStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ziraat/status/${paymentRef}`,
        );
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || 'status_fetch_failed');
        }
        const data = await response.json();
        setPaymentInfo(data);

        if (data.status === 'completed') {
          setPaymentStatus('success');
          setPaymentError(null);
          await syncMembership();
          return true;
        }
        if (['failed', '3d_failed'].includes(data.status)) {
          setPaymentStatus('failed');
          setPaymentError(data.error || t('membership.paymentFailed'));
          return true;
        }
        return false;
      } catch (err) {
        console.error('Payment status check failed:', err);
        if (pollRef.current >= MAX_POLLS) {
          setPaymentStatus('pending');
          setPaymentError(err.message || t('membership.paymentPendingHint'));
        }
        return false;
      }
    };

    const poll = async () => {
      setPaymentStatus('loading');
      const done = await checkStatus();
      if (done) return;

      pollRef.current += 1;
      if (pollRef.current < MAX_POLLS) {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } else {
        setPaymentStatus('pending');
      }
    };

    pollRef.current = 0;
    poll();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showPaymentBanner, paymentRef, urlError, authLoading, user?.id, t]);

  const dismissPaymentBanner = () => {
    setPaymentStatus(null);
    setPaymentInfo(null);
    setPaymentError(null);
    navigate(membershipPath, { replace: true });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const tiersRes = await api.get('/membership/tiers');
      setTiers(tiersRes.data.tiers || {});

      if (user) {
        try {
          const subRes = await api.get('/membership/my-subscription');
          setCurrentSubscription(subRes.data.subscription);
          setUserPoints(subRes.data.points);
        } catch (e) {
          // No subscription
        }
      }
    } catch (error) {
      console.error('Failed to fetch membership data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPaymentDialog = (tier) => {
    if (!user) {
      navigate('/login?redirect=/membership');
      return;
    }
    setSelectedTier(tier);
    setZiraatCard(emptyCard);
    setPaymentDialogOpen(true);
  };

  const getTierPrice = (config) => {
    if (isTurkish && config.price_monthly_try != null) {
      const tryPrice = billingPeriod === 'monthly' ? config.price_monthly_try : config.price_yearly_try;
      return { value: tryPrice, currency: 'TRY', symbol: '₺' };
    }
    const usdPrice = billingPeriod === 'monthly' ? config.price_monthly : config.price_yearly;
    return { value: usdPrice, currency: 'USD', symbol: '$' };
  };

  const handleSubscribe = async () => {
    if (!selectedTier) return;

    if (!ziraatCard.holder || !ziraatCard.number || !ziraatCard.expiryMonth || !ziraatCard.expiryYear || !ziraatCard.cvv) {
      toast.error(t('membership.fillCardFields'));
      return;
    }

    setSubscribing(selectedTier);
    try {
      const res = await api.post('/membership/subscribe', {
        tier: selectedTier,
        billing_period: billingPeriod,
        card_number: ziraatCard.number.replace(/\s/g, ''),
        card_holder: ziraatCard.holder,
        expiry_month: ziraatCard.expiryMonth,
        expiry_year: ziraatCard.expiryYear,
        cvv: ziraatCard.cvv,
        installment: parseInt(ziraatCard.installment, 10) || 1,
      });

      if (res.data.html_form) {
        setPaymentDialogOpen(false);
        const newWindow = window.open('', '_self');
        newWindow.document.write(res.data.html_form);
        newWindow.document.close();
        return;
      }

      throw new Error('3D form missing');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(resolveMembershipError(detail, t));
    } finally {
      setSubscribing(null);
    }
  };

  const tierIcons = {
    bronze: <Star className="w-8 h-8" />,
    silver: <Zap className="w-8 h-8" />,
    gold: <Crown className="w-8 h-8" />,
  };

  const tierColors = {
    bronze: 'from-amber-600 to-amber-800',
    silver: 'from-slate-400 to-slate-600',
    gold: 'from-yellow-400 to-yellow-600',
  };

  const tierBorders = {
    bronze: 'border-amber-500',
    silver: 'border-slate-400',
    gold: 'border-yellow-500',
  };

  const selectedConfig = selectedTier ? tiers[selectedTier] : null;
  const selectedPrice = selectedConfig ? getTierPrice(selectedConfig) : null;

  const tierLabel = paymentInfo?.membership_tier
    ? getMembershipTierLabel(paymentInfo.membership_tier, lang, t)
    : null;

  const bannerStyles = {
    loading: 'bg-blue-50 border-blue-300 text-blue-900',
    success: 'bg-green-50 border-green-400 text-green-900',
    failed: 'bg-red-50 border-red-400 text-red-900',
    pending: 'bg-amber-50 border-amber-400 text-amber-900',
  };

  const bannerIcons = {
    loading: <Loader2 className="w-6 h-6 animate-spin shrink-0" />,
    success: <CheckCircle className="w-6 h-6 shrink-0 text-green-600" />,
    failed: <XCircle className="w-6 h-6 shrink-0 text-red-600" />,
    pending: <AlertCircle className="w-6 h-6 shrink-0 text-amber-600" />,
  };

  const bannerTitles = {
    loading: t('membership.paymentChecking'),
    success: t('membership.paymentSuccessTitle'),
    failed: t('membership.paymentFailedTitle'),
    pending: t('membership.paymentPendingTitle'),
  };

  const displayStatus = paymentStatus || (paymentRef ? 'loading' : urlError ? 'failed' : null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20" data-testid="membership-page">
      <PageSeo title={t('membership.pageTitle')} description={t('membership.pageSubtitle')} segments={['membership']} />
      {showPaymentBanner && displayStatus && (
        <div
          className={`sticky top-0 z-50 border-b-2 px-4 py-4 shadow-md ${bannerStyles[displayStatus]}`}
          data-testid="membership-payment-banner"
        >
          <div className="container mx-auto max-w-3xl">
            <div className="flex items-start gap-3">
              {bannerIcons[displayStatus]}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-lg">{bannerTitles[displayStatus]}</p>
                {displayStatus === 'loading' && (
                  <p className="text-sm mt-1 opacity-90">{t('membership.paymentCheckingDesc')}</p>
                )}
                {displayStatus === 'success' && (
                  <p className="text-sm mt-1 opacity-90">{t('membership.paymentSuccessDesc')}</p>
                )}
                {displayStatus === 'failed' && (
                  <p className="text-sm mt-1 opacity-90">{t('membership.paymentFailedDesc')}</p>
                )}
                {displayStatus === 'pending' && (
                  <p className="text-sm mt-1 opacity-90">{t('membership.paymentPendingDesc')}</p>
                )}

                <div className="mt-3 rounded-lg bg-white/70 border border-current/20 p-3 text-sm space-y-1 font-mono">
                  <p><span className="font-sans font-semibold">{t('membership.bankResponse')}:</span> {paymentInfo?.status || (displayStatus === 'loading' ? '...' : displayStatus)}</p>
                  {paymentRef && (
                    <p><span className="font-sans font-semibold">{t('membership.paymentRef')}:</span> {paymentRef}</p>
                  )}
                  {tierLabel && (
                    <p><span className="font-sans font-semibold">{t('membership.membershipTier')}:</span> {tierLabel}</p>
                  )}
                  {paymentInfo?.amount != null && (
                    <p>
                      <span className="font-sans font-semibold">{t('membership.paidAmount')}:</span>{' '}
                      ₺{Number(paymentInfo.amount).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  {paymentError && (
                    <p className="text-red-700 break-words">
                      <span className="font-sans font-semibold">{t('membership.errorDetail')}:</span> {paymentError}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={dismissPaymentBanner}
                className="shrink-0 p-1 rounded hover:bg-black/10"
                aria-label={t('membership.closeBanner', 'Kapat')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && !showPaymentBanner && (
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      )}

      {(!loading || showPaymentBanner) && (
      <>
      <section className="py-16 md:py-24 text-center">
        <div className="container mx-auto px-4 md:px-8">
          <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">
            {t('membership.program')}
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-4">
            {t('membership.pageTitle')}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            {t('membership.pageSubtitle')}
          </p>

          {currentSubscription && (
            <div className="mb-8 p-4 rounded-2xl bg-primary/10 border border-primary/20 max-w-md mx-auto">
              <div className="flex items-center justify-center gap-3">
                {tierIcons[currentSubscription.tier]}
                <div className="text-left">
                  <p className="font-semibold">{t('membership.yourActiveMembership')}</p>
                  <p className="text-sm text-muted-foreground">
                    {getMembershipTierLabel(currentSubscription.tier, lang, t)} {t('membership.member')}
                  </p>
                </div>
                {userPoints && (
                  <Badge variant="secondary" className="ml-4">
                    {userPoints.available_points} {t('membership.points')}
                  </Badge>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-center gap-4 mb-12">
            <Label htmlFor="billing-toggle" className={billingPeriod === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}>
              {t('membership.monthly')}
            </Label>
            <Switch
              id="billing-toggle"
              checked={billingPeriod === 'yearly'}
              onCheckedChange={(checked) => setBillingPeriod(checked ? 'yearly' : 'monthly')}
            />
            <Label htmlFor="billing-toggle" className={billingPeriod === 'yearly' ? 'font-semibold' : 'text-muted-foreground'}>
              {t('membership.yearly')}
              <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">
                {t('membership.save16')}
              </Badge>
            </Label>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {Object.entries(tiers).map(([tier, config]) => {
              const isCurrentTier = currentSubscription?.tier === tier;
              const price = getTierPrice(config);
              const features = getMembershipTierFeatures(tier, config, t, lang);
              const tierDisplayName = getMembershipTierDisplayName(tier, config, t, lang);
              const billingSuffix = billingPeriod === 'monthly'
                ? t('membership.perMonth')
                : t('membership.perYear');

              return (
                <Card
                  key={tier}
                  className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
                    tier === 'gold' ? 'ring-2 ring-yellow-500 scale-105' : ''
                  } ${isCurrentTier ? `${tierBorders[tier]} border-2` : ''}`}
                  data-testid={`tier-${tier}`}
                >
                  {tier === 'gold' && (
                    <div className="absolute top-0 right-0 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      {t('membership.mostPopular')}
                    </div>
                  )}

                  <CardHeader className={`text-center bg-gradient-to-br ${tierColors[tier]} text-white p-6`}>
                    <div className="mx-auto mb-2">{tierIcons[tier]}</div>
                    <CardTitle className="text-2xl font-bold">
                      {tierDisplayName}
                    </CardTitle>
                    <CardDescription className="text-white/80">
                      {config.points_multiplier}x {t('membership.pointsMultiplier')}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-6">
                    <div className="text-center mb-6">
                      <span className="text-4xl font-bold">
                        {price.symbol}
                        {Number(price.value).toLocaleString(isTurkish ? 'tr-TR' : 'en-US', { maximumFractionDigits: 0 })}
                      </span>
                      <span className="text-muted-foreground">
                        /{billingSuffix}
                      </span>
                    </div>

                    <ul className="space-y-3">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>

                  <CardFooter className="p-6 pt-0">
                    <Button
                      className="w-full rounded-full"
                      variant={tier === 'gold' ? 'default' : 'outline'}
                      disabled={isCurrentTier || subscribing !== null}
                      onClick={() => openPaymentDialog(tier)}
                      data-testid={`subscribe-${tier}`}
                    >
                      {subscribing === tier ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {isCurrentTier ? t('membership.currentPlan') : t('membership.subscribe')}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary/50">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-serif font-bold text-center mb-12">{t('membership.benefitsTitle')}</h2>
          <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Truck className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t('membership.freeShipping')}</h3>
              <p className="text-sm text-muted-foreground">{t('membership.freeShippingDesc')}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Gift className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t('membership.earnPoints')}</h3>
              <p className="text-sm text-muted-foreground">{t('membership.earnPointsDesc')}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t('membership.earlyAccess')}</h3>
              <p className="text-sm text-muted-foreground">{t('membership.earlyAccessDesc')}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Headphones className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t('membership.vipSupport')}</h3>
              <p className="text-sm text-muted-foreground">{t('membership.vipSupportDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <Card>
            <CardHeader>
              <CardTitle className="text-center">{t('membership.sellerCommissionTitle')}</CardTitle>
              <CardDescription className="text-center">{t('membership.sellerCommissionDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-4 rounded-lg bg-secondary">
                  <p className="text-sm text-muted-foreground mb-1">{t('membership.standard')}</p>
                  <p className="text-2xl font-bold">%5</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-700 mb-1">{t('membership.tierBronze')}</p>
                  <p className="text-2xl font-bold text-amber-700">%4.5</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-300">
                  <p className="text-sm text-slate-600 mb-1">{t('membership.tierSilver')}</p>
                  <p className="text-2xl font-bold text-slate-600">%4</p>
                </div>
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-300">
                  <p className="text-sm text-yellow-700 mb-1">{t('membership.tierGold')}</p>
                  <p className="text-2xl font-bold text-yellow-700">%3.5</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      </>
      )}

      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <DialogTitle>{t('membership.cardPaymentTitle')}</DialogTitle>
                <DialogDescription>{t('membership.cardPaymentDesc')}</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {selectedPrice && (
            <p className="text-sm text-muted-foreground text-center">
              {selectedPrice.symbol}
              {Number(selectedPrice.value).toLocaleString(isTurkish ? 'tr-TR' : 'en-US', { maximumFractionDigits: 0 })}
              {' '}
              / {billingPeriod === 'monthly' ? t('membership.monthly') : t('membership.yearly')}
            </p>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="mem_card_holder">{t('membership.cardHolder')}</Label>
              <Input
                id="mem_card_holder"
                placeholder="AD SOYAD"
                value={ziraatCard.holder}
                onChange={(e) => setZiraatCard({ ...ziraatCard, holder: e.target.value.toUpperCase() })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="mem_card_number">{t('membership.cardNumber')}</Label>
              <Input
                id="mem_card_number"
                placeholder="1234 5678 9012 3456"
                value={ziraatCard.number}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '').slice(0, 16);
                  value = value.replace(/(\d{4})(?=\d)/g, '$1 ');
                  setZiraatCard({ ...ziraatCard, number: value });
                }}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="mem_expiry_month">{t('membership.expiryMonth')}</Label>
                <select
                  id="mem_expiry_month"
                  value={ziraatCard.expiryMonth}
                  onChange={(e) => setZiraatCard({ ...ziraatCard, expiryMonth: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                >
                  <option value="">AA</option>
                  {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="mem_expiry_year">{t('membership.expiryYear')}</Label>
                <select
                  id="mem_expiry_year"
                  value={ziraatCard.expiryYear}
                  onChange={(e) => setZiraatCard({ ...ziraatCard, expiryYear: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                >
                  <option value="">YY</option>
                  {Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() + i).toString().slice(-2)).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="mem_cvv">{t('membership.cvv')}</Label>
                <Input
                  id="mem_cvv"
                  type="password"
                  placeholder="***"
                  maxLength={4}
                  value={ziraatCard.cvv}
                  onChange={(e) => setZiraatCard({ ...ziraatCard, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} disabled={subscribing !== null}>
              {t('common.cancel', 'İptal')}
            </Button>
            <Button onClick={handleSubscribe} disabled={subscribing !== null}>
              {subscribing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('membership.payNow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembershipPage;
